/**
 * System Reset Tool - Admin page to clear test data before going live
 * Access at: /admin/system-reset
 * 
 * PRESERVES:
 * - All Bula Coffee Pass purchases
 * - All Coffee Pass members
 * - All Coffee Pass redemptions
 * - Menu items, Staff, Customers
 * 
 * DELETES:
 * - All test orders
 * - Print jobs
 * - Sales reports
 * - Activity logs (except Coffee Pass)
 */
import { useState } from 'react';
import { 
  Trash2, Shield, AlertTriangle, RefreshCw, CheckCircle, 
  Coffee, FileText, Printer, BarChart2, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

const ADMIN_PIN = "GOLIVE2024";

export default function SystemResetPage() {
  const [pin, setPin] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [resetResults, setResetResults] = useState(null);

  // Authenticate with PIN
  const handleAuth = () => {
    if (pin === ADMIN_PIN) {
      setAuthenticated(true);
      toast.success('Authenticated successfully');
      loadPreview();
    } else {
      toast.error('Invalid PIN');
    }
  };

  // Load preview of what will be deleted
  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/api/admin/system-reset/preview?admin_pin=${ADMIN_PIN}`);
      setPreview(res.data);
    } catch (err) {
      toast.error('Failed to load preview');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Execute the reset
  const executeReset = async () => {
    if (!window.confirm('⚠️ FINAL WARNING ⚠️\n\nThis will DELETE all test orders, sales reports, and activity logs.\n\nBula Coffee Pass data will be PRESERVED.\n\nAre you absolutely sure?')) {
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(
        `/api/admin/system-reset/execute?admin_pin=${ADMIN_PIN}&confirm=DELETE_ALL_TEST_DATA`
      );
      setResetResults(res.data);
      setResetComplete(true);
      toast.success('System reset completed successfully!');
    } catch (err) {
      toast.error('Reset failed: ' + (err.response?.data?.detail || err.message));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // PIN Entry Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">System Reset Tool</h1>
            <p className="text-gray-400 mt-2">Enter admin PIN to continue</p>
          </div>

          <div className="space-y-4">
            <Input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Enter admin PIN"
              className="h-12 text-center text-xl bg-gray-700 border-gray-600 text-white"
              onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
            />
            <Button
              onClick={handleAuth}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
            >
              <Shield className="w-5 h-5 mr-2" />
              Authenticate
            </Button>
          </div>

          <p className="text-center text-gray-500 text-sm mt-6">
            This tool is for administrator use only
          </p>
        </div>
      </div>
    );
  }

  // Reset Complete Screen
  if (resetComplete && resetResults) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-8">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Reset Complete!</h1>
          <p className="text-gray-400 mb-8">All test data has been cleared. Ready for live operations.</p>

          <div className="bg-gray-700 rounded-xl p-6 mb-6 text-left">
            <h3 className="text-white font-semibold mb-4">What was deleted:</h3>
            <div className="space-y-2 text-gray-300">
              <div className="flex justify-between">
                <span>Orders deleted:</span>
                <span className="text-red-400">{resetResults.results?.orders_deleted || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Print jobs deleted:</span>
                <span className="text-red-400">{resetResults.results?.print_jobs_deleted || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Sales reports deleted:</span>
                <span className="text-red-400">{resetResults.results?.daily_sales_deleted || 0}</span>
              </div>
            </div>
          </div>

          <div className="bg-green-900/30 border border-green-600 rounded-xl p-6 mb-6 text-left">
            <h3 className="text-green-400 font-semibold mb-4 flex items-center">
              <Coffee className="w-5 h-5 mr-2" />
              Coffee Pass Data Preserved:
            </h3>
            <div className="space-y-2 text-green-300">
              <div className="flex justify-between">
                <span>Coffee Pass members:</span>
                <span>{resetResults.preserved?.coffee_pass_members || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Coffee Pass redemptions:</span>
                <span>{resetResults.preserved?.coffee_pass_redemptions || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>Coffee Pass orders:</span>
                <span>{resetResults.preserved?.coffee_pass_orders || 0}</span>
              </div>
            </div>
          </div>

          <Button
            onClick={() => window.location.href = '/admin'}
            className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            Go to Admin Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Main Preview Screen
  return (
    <div className="min-h-screen bg-gray-900 p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Trash2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">System Reset Tool</h1>
          <p className="text-gray-400 mt-2">Clear all test data before going live</p>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-900/30 border border-yellow-600 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-400">Warning: This action cannot be undone!</h3>
              <p className="text-yellow-200/80 text-sm mt-1">
                This will permanently delete all test orders, sales reports, and activity logs.
                Bula Coffee Pass data will be preserved.
              </p>
            </div>
          </div>
        </div>

        {loading && !preview ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 animate-spin text-indigo-400 mx-auto mb-4" />
            <p className="text-gray-400">Loading preview...</p>
          </div>
        ) : preview ? (
          <>
            {/* Will Be Deleted */}
            <div className="bg-red-900/20 border border-red-600 rounded-xl p-6 mb-6">
              <h2 className="text-xl font-bold text-red-400 mb-4 flex items-center">
                <Trash2 className="w-5 h-5 mr-2" />
                Will Be DELETED
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <FileText className="w-4 h-4" />
                    <span className="text-sm">Orders</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">{preview.will_delete?.orders || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Printer className="w-4 h-4" />
                    <span className="text-sm">Print Jobs</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">{preview.will_delete?.print_jobs || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <BarChart2 className="w-4 h-4" />
                    <span className="text-sm">Sales Reports</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">{preview.will_delete?.daily_sales_reports || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-gray-400 mb-1">
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">Activity Logs</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">{preview.will_delete?.activity_logs || 0}</span>
                </div>
              </div>
            </div>

            {/* Will Be Preserved */}
            <div className="bg-green-900/20 border border-green-600 rounded-xl p-6 mb-6">
              <h2 className="text-xl font-bold text-green-400 mb-4 flex items-center">
                <Coffee className="w-5 h-5 mr-2" />
                Will Be PRESERVED
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Coffee Pass Orders</div>
                  <span className="text-2xl font-bold text-green-400">{preview.will_preserve?.coffee_pass_orders || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Coffee Pass Members</div>
                  <span className="text-2xl font-bold text-green-400">{preview.will_preserve?.coffee_pass_members || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Coffee Pass Redemptions</div>
                  <span className="text-2xl font-bold text-green-400">{preview.will_preserve?.coffee_pass_redemptions || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Menu Items</div>
                  <span className="text-2xl font-bold text-green-400">{preview.will_preserve?.menu_items || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Staff Members</div>
                  <span className="text-2xl font-bold text-green-400">{preview.will_preserve?.staff_members || 0}</span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="text-sm text-gray-400 mb-1">Customer Contacts</div>
                  <span className="text-2xl font-bold text-green-400">{preview.will_preserve?.customers_contacts || 0}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                onClick={loadPreview}
                disabled={loading}
                variant="outline"
                className="flex-1 h-12 border-gray-600 text-gray-300 hover:bg-gray-800"
              >
                <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Preview
              </Button>
              <Button
                onClick={executeReset}
                disabled={loading}
                className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white"
              >
                {loading ? (
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-5 h-5 mr-2" />
                )}
                Execute Reset
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
